"use client";

import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useState } from "react";
import { toast } from "sonner";
import { getCurrentSessionIdentity } from "@/actions/auth";
import {
  SESSION_IDENTITY_QUERY_KEY,
  useAuthState,
} from "@/hooks/use-auth-state";
import { publishWatchActivity } from "@/hooks/use-live-activity-commands";
import { setNativeSessionOwner } from "@/hooks/use-native-session-owner";
import { usePushNotifications } from "@/hooks/use-push-notifications";
import { captureClientEvent } from "@/lib/analytics/client";
import { ANALYTICS_EVENTS } from "@/lib/analytics/events";
import { authClient } from "@/lib/auth-client";
import { clearPrivateBrowserState } from "@/lib/client-private-state";
import {
  freezeMovementActivity,
  readMovementJournal,
  writeMovementJournal,
} from "@/lib/movement/session-journal";
import { workoutNativeSessionId } from "@/lib/native/workout-identity";
import {
  freezeWorkoutSession,
  readWorkoutJournal,
  writeWorkoutJournal,
} from "@/lib/weight-training/session-journal";
import { wtKeys } from "@/lib/weight-training-keys";

type SignOutMethod = "manual" | "switch_account";

/**
 * The one sign-out sequence, shared by every surface that offers it.
 *
 * Order matters: the push token is dropped while the session still exists (so a
 * shared device stops receiving the previous user's notifications), then the
 * cookie is cleared through the browser endpoint so Better Auth's `Set-Cookie`
 * is actually applied, then every client-side cache is wiped. The final
 * navigation is a full `location.replace`, not a router push — a client
 * navigation would keep the old React tree, and any account-scoped state that
 * survived the cache clear with it.
 */
export function useSignOut() {
  const queryClient = useQueryClient();
  const accountId = useAuthState().identity?.id;
  const {
    unregister: unregisterPush,
    requestPermission: restorePush,
    isRegistered: hadPush,
    permission: pushPermission,
  } = usePushNotifications();
  const [isSigningOut, setIsSigningOut] = useState(false);

  const signOut = useCallback(
    async ({
      method = "manual",
      redirectTo = "/",
    }: {
      method?: SignOutMethod;
      redirectTo?: string;
    } = {}) => {
      setIsSigningOut(true);
      const stoppedAt = Date.now();
      try {
        if (accountId) {
          const recording = readMovementJournal(window.localStorage, accountId);
          if (recording)
            writeMovementJournal(
              window.localStorage,
              accountId,
              freezeMovementActivity(recording, stoppedAt),
            );
        }
      } catch {
        toast.error(
          "The recording could not be secured for sign-out. Keep the app open and try again.",
        );
        // Retain owner-scoped data, but never trap the user in a signed-in account.
      }

      // Keep the two journals independent: damaged movement data must not leave
      // the strength clock running through another account's session.
      try {
        if (accountId) {
          const workout = readWorkoutJournal(window.localStorage, accountId);
          if (workout?.workoutId && workout.startedAt) {
            const frozen = freezeWorkoutSession(workout, stoppedAt);
            writeWorkoutJournal(window.localStorage, accountId, frozen);
            queryClient.setQueryData(wtKeys.activeWorkout(accountId), frozen);
            // Persist the Watch terminal before revoking its old account epoch.
            // Failed delivery remains recoverable from the owner's journal.
            await publishWatchActivity({
              activity: "workout",
              state: "ended",
              accountId,
              sessionId: workoutNativeSessionId(accountId, frozen.workoutId),
              startedAtMs: Date.parse(frozen.startedAt),
              endedAtMs: Date.parse(frozen.endedAt),
              disposition: frozen.disposition ?? "completed",
            });
          }
        }
      } catch {
        toast.error(
          "Workout recovery is pending. Sign back in to this account to retry synchronization.",
        );
      }

      try {
        await setNativeSessionOwner(null);
      } catch {
        toast.error(
          "Some native activity cleanup is pending. Reopen Hiive to retry.",
        );
      }

      try {
        await unregisterPush();
      } catch {
        // Best effort: a stale token is cleaned up server-side on the next
        // failed send, and must never block sign-out.
      }

      try {
        const result = await authClient.signOut();
        if (result.error)
          throw new Error(result.error.message ?? "Failed to sign out");
      } catch (error) {
        // Re-read authority before restoring services: an interrupted response
        // may still have signed out remotely, or another account may be active.
        let current: Awaited<ReturnType<typeof getCurrentSessionIdentity>>;
        try {
          current = await getCurrentSessionIdentity();
        } catch {
          toast.error(
            "Could not confirm your account. Reconnect before starting another recording.",
          );
          setIsSigningOut(false);
          return false;
        }
        queryClient.setQueryData(SESSION_IDENTITY_QUERY_KEY, current);
        try {
          await setNativeSessionOwner(current?.id ?? null);
          if (
            current?.id === accountId &&
            hadPush &&
            pushPermission === "granted"
          )
            await restorePush();
        } catch {
          toast.error(
            "Native services could not be restored. Reopen Hiive to retry.",
          );
        }
        if (current && current.id === accountId) {
          toast.error(
            error instanceof Error ? error.message : "Failed to sign out",
          );
          setIsSigningOut(false);
          return false;
        }
        // The original account is gone. Clear its caches and reload even when
        // the sign-out response was lost; never restore it over a new account.
      }

      queryClient.clear();
      await clearPrivateBrowserState();

      captureClientEvent(ANALYTICS_EVENTS.AUTH_SIGNED_OUT, { method });
      window.location.replace(redirectTo);
      return true;
    },
    [
      accountId,
      queryClient,
      unregisterPush,
      restorePush,
      hadPush,
      pushPermission,
    ],
  );

  return { signOut, isSigningOut };
}
